// frontend/src/components/celebration-chat/TagReviewCard.tsx
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tags, Check, X, Plus } from 'lucide-react';
import { tagCategoryService } from '@/lib/api/tagCategory.service';
import type { TagCategoryWithValues } from '@/types/tag.types';

interface TagReviewCardProps {
  tags: Record<string, string[]>;
  onConfirm?: (selectedTags: Record<string, string[]>) => void;
  onSkip?: () => void;
  persistenceKey?: string;
}

const toSelectedTagSets = (input: Record<string, string[]>): Record<string, Set<string>> => {
  const initial: Record<string, Set<string>> = {};
  for (const [category, values] of Object.entries(input)) {
    initial[category] = new Set(values);
  }
  return initial;
};

const toSerializableSelectedTags = (input: Record<string, Set<string>>): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  for (const [category, valueSet] of Object.entries(input)) {
    if (valueSet.size > 0) {
      result[category] = Array.from(valueSet);
    }
  }
  return result;
};

const getPersistenceStorageKey = (persistenceKey?: string): string | null => {
  if (!persistenceKey) return null;
  return `celebration_chat_tag_review_${persistenceKey}`;
};

const loadPersistedSelections = (
  tags: Record<string, string[]>,
  persistenceKey?: string,
): Record<string, Set<string>> => {
  const storageKey = getPersistenceStorageKey(persistenceKey);
  if (!storageKey) {
    return toSelectedTagSets(tags);
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return toSelectedTagSets(tags);
    }

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const normalized: Record<string, string[]> = {};

    for (const [category, values] of Object.entries(parsed)) {
      if (Array.isArray(values)) {
        normalized[category] = values.map((value) => String(value)).filter(Boolean);
      }
    }

    if (Object.keys(normalized).length === 0) {
      return toSelectedTagSets(tags);
    }

    return toSelectedTagSets(normalized);
  } catch {
    return toSelectedTagSets(tags);
  }
};

export const TagReviewCard: React.FC<TagReviewCardProps> = ({ tags, onConfirm, onSkip, persistenceKey }) => {
  const { t } = useTranslation('celebrations');
  const normalizeCategoryKey = (value: string): string =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');

  // Initialize selected state — all tags start as selected
  const [selectedTags, setSelectedTags] = useState<Record<string, Set<string>>>(() => loadPersistedSelections(tags, persistenceKey));
  const [categoriesWithValues, setCategoriesWithValues] = useState<TagCategoryWithValues[]>([]);
  const [openAddForCategory, setOpenAddForCategory] = useState<string | null>(null);
  const [pendingAddValue, setPendingAddValue] = useState<string>('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const categories = await tagCategoryService.getCategoriesWithValues();
        if (active) {
          setCategoriesWithValues(categories);
        }
      } catch {
        if (active) {
          setCategoriesWithValues([]);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const toggleTag = useCallback((category: string, value: string) => {
    setSelectedTags((prev) => {
      const updated = { ...prev };
      const categorySet = new Set(updated[category] || []);

      if (categorySet.has(value)) {
        categorySet.delete(value);
      } else {
        categorySet.add(value);
      }

      if (categorySet.size === 0) {
        delete updated[category];
      } else {
        updated[category] = categorySet;
      }

      return updated;
    });
  }, []);

  const addTagToCategory = useCallback((category: string, value: string) => {
    const normalized = value.trim();
    if (!normalized) return;

    setSelectedTags((prev) => {
      const updated = { ...prev };
      const categorySet = new Set(updated[category] || []);
      categorySet.add(normalized);
      updated[category] = categorySet;
      return updated;
    });
  }, []);

  // Only re-load when the persistence key changes (i.e. tag content actually changed).
  // Do NOT include `tags` as a dep: richData.tags gets a new object reference on every
  // parent re-render even when content is identical.  Including it would cause the load
  // effect to fire *after* the user toggles a tag but *before* the save effect writes to
  // localStorage, resetting the toggle and then overwriting localStorage with the old value.
  useEffect(() => {
    setSelectedTags(loadPersistedSelections(tags, persistenceKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistenceKey]);

  useEffect(() => {
    const storageKey = getPersistenceStorageKey(persistenceKey);
    if (!storageKey) return;

    const serializable = toSerializableSelectedTags(selectedTags);
    window.localStorage.setItem(storageKey, JSON.stringify(serializable));
  }, [selectedTags, persistenceKey]);

  const handleConfirm = useCallback(() => {
    const result = toSerializableSelectedTags(selectedTags);

    const storageKey = getPersistenceStorageKey(persistenceKey);
    if (storageKey) {
      window.localStorage.removeItem(storageKey);
    }

    onConfirm?.(result);
  }, [selectedTags, onConfirm, persistenceKey]);

  const handleSkip = useCallback(() => {
    const storageKey = getPersistenceStorageKey(persistenceKey);
    if (storageKey) {
      window.localStorage.removeItem(storageKey);
    }
    onSkip?.();
  }, [onSkip, persistenceKey]);

  const normalizedCategoryMap = useMemo(() => {
    const map = new Map<string, TagCategoryWithValues>();
    for (const category of categoriesWithValues) {
      map.set(normalizeCategoryKey(category.name), category);
    }
    return map;
  }, [categoriesWithValues]);

  const allCategoryValues = useMemo(() => (
    categoriesWithValues.flatMap((category) => (
      category.values.map((value) => ({
        ...value,
        category_name: value.category_name ?? category.name,
      }))
    ))
  ), [categoriesWithValues]);

  const getCategoryMatch = useCallback((categoryLabel: string): TagCategoryWithValues | undefined => {
    const normalizedLabel = normalizeCategoryKey(categoryLabel);
    const exact = normalizedCategoryMap.get(normalizedLabel);
    if (exact) return exact;

    // Handle small label variations (e.g., "Cultural" vs "Culture")
    for (const [key, category] of normalizedCategoryMap.entries()) {
      if (key.includes(normalizedLabel) || normalizedLabel.includes(key)) {
        return category;
      }
    }

    return undefined;
  }, [normalizedCategoryMap]);

  const handleAddValue = useCallback((category: string) => {
    const query = pendingAddValue.trim();
    if (!query) return;

    const categoryMatch = getCategoryMatch(category);
    const existingValues = categoryMatch?.values ?? [];
    const matched = existingValues.find((tagValue) =>
      tagValue.value.toLowerCase() === query.toLowerCase()
    );

    const fallbackMatched = !matched
      ? allCategoryValues.find((tagValue) => tagValue.value.toLowerCase() === query.toLowerCase())
      : undefined;

    const valueToAdd = matched?.value ?? fallbackMatched?.value;
    if (!valueToAdd) {
      return;
    }

    addTagToCategory(category, valueToAdd);

    setPendingAddValue('');
    setOpenAddForCategory(null);
  }, [pendingAddValue, getCategoryMatch, allCategoryValues, addTagToCategory]);

  const displayValuesByCategory = useMemo(() => {
    const allCategories = new Set([
      ...Object.keys(tags),
      ...Object.keys(selectedTags),
    ]);

    const merged: Record<string, string[]> = {};
    for (const category of allCategories) {
      const baseValues = tags[category] ?? [];
      const addedValues = Array.from(selectedTags[category] ?? []);
      merged[category] = Array.from(new Set([...baseValues, ...addedValues]));
    }

    return merged;
  }, [tags, selectedTags]);

  const totalSelected = Object.values(selectedTags).reduce(
    (sum, set) => sum + set.size,
    0
  );

  const totalAvailable = Object.values(tags).reduce(
    (sum, vals) => sum + vals.length,
    0
  );

  return (
    <Card className="border-primary/20 bg-primary/5 max-w-md">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Tags className="h-5 w-5 text-primary" />
          {t('chat.tagReview.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t('chat.tagReview.selectionCount', { selected: totalSelected, available: totalAvailable })}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(displayValuesByCategory).map(([category, values]) => (
          <div key={category}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium text-muted-foreground">{category}</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {values.map((value) => {
                const isSelected = selectedTags[category]?.has(value) ?? false;
                return (
                  <Badge
                    key={`${category}-${value}`}
                    variant="outline"
                    className={`cursor-pointer transition-all select-none ${
                      isSelected
                        ? 'bg-primary/15 text-primary border-primary/50 font-medium hover:bg-primary/25'
                        : 'opacity-50 line-through text-muted-foreground hover:opacity-70'
                    }`}
                    onClick={() => toggleTag(category, value)}
                  >
                    {value}
                  </Badge>
                );
              })}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 w-6 p-0 rounded-full"
                aria-label={t('chat.tagReview.addTagFor', { category })}
                title={t('chat.tagReview.addTagFor', { category })}
                onClick={() => {
                  if (openAddForCategory === category) {
                    setOpenAddForCategory(null);
                    setPendingAddValue('');
                  } else {
                    setOpenAddForCategory(category);
                    setPendingAddValue('');
                  }
                }}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {openAddForCategory === category && (
              <div className="mt-2 space-y-2">
                <input
                  type="text"
                  value={pendingAddValue}
                  onChange={(event) => setPendingAddValue(event.target.value)}
                  placeholder={t('chat.tagReview.searchPlaceholder')}
                  aria-label={t('chat.tagReview.searchTagFor', { category })}
                  title={t('chat.tagReview.searchTagFor', { category })}
                  className="h-8 flex-1 rounded-md border bg-background px-2 text-sm"
                />
                {pendingAddValue.trim() ? (
                  <div className="max-h-28 overflow-y-auto rounded-md border bg-background">
                    {(() => {
                      const categoryMatch = getCategoryMatch(category);
                      const existingValues = categoryMatch?.values ?? [];
                      const selectedSet = selectedTags[category] ?? new Set<string>();
                      const query = pendingAddValue.trim().toLowerCase();

                      let filteredValues = existingValues.filter((tagValue) => {
                        if (selectedSet.has(tagValue.value)) return false;
                        return tagValue.value.toLowerCase().includes(query);
                      });

                      if (!categoryMatch) {
                        const dedupe = new Set(filteredValues.map((tagValue) => tagValue.value.toLowerCase()));
                        for (const tagValue of allCategoryValues) {
                          if (selectedSet.has(tagValue.value)) continue;
                          if (!tagValue.value.toLowerCase().includes(query)) continue;
                          const dedupeKey = tagValue.value.toLowerCase();
                          if (dedupe.has(dedupeKey)) continue;
                          dedupe.add(dedupeKey);
                          filteredValues.push(tagValue);
                        }
                      }

                      if (filteredValues.length === 0) {
                        return <p className="px-2 py-1.5 text-xs text-muted-foreground">{t('chat.tagReview.noMatchingTags')}</p>;
                      }

                      return filteredValues.slice(0, 12).map((tagValue) => (
                        <button
                          key={`${category}-${tagValue.id}`}
                          type="button"
                          className="block w-full px-2 py-1.5 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            addTagToCategory(category, tagValue.value);
                            setPendingAddValue('');
                            setOpenAddForCategory(null);
                          }}
                        >
                          {tagValue.value}
                        </button>
                      ));
                    })()}
                  </div>
                ) : (
                  <p className="px-1 text-xs text-muted-foreground">{t('chat.tagReview.startTypingHint')}</p>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleAddValue(category)}
                  disabled={!pendingAddValue.trim()}
                >
                  {t('chat.tagReview.addSelected')}
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
      {(onConfirm || onSkip) && (
        <CardFooter className="flex gap-2 pt-2">
          {onConfirm && (
            <Button size="sm" onClick={handleConfirm} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700">
              <Check className="h-4 w-4 mr-1" />
              {totalSelected > 0 ? t('chat.tagReview.confirmTagsWithCount', { count: totalSelected }) : t('chat.tagReview.confirmTags')}
            </Button>
          )}
          {onSkip && (
            <Button size="sm" variant="outline" onClick={handleSkip} className="flex-1">
              <X className="h-4 w-4 mr-1" />
              {t('chat.tagReview.skipTags')}
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
};
